import { Component, Injector, Input, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { WidgetBaseComponent } from '../../widgetBase';

/**
 * CountrySpecifics Widget
 */
@Component({
  selector: 'country-specifics-widget',
  templateUrl: './country-specifics-widget.component.html',
  styleUrls: ['../../common/css/general.scss', '../../common/css/text-and-list.scss', './country-specifics-widget.component.scss']
})
export class CountrySpecificsWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Should custom labels be disabled?
   */
  disableCustomLabels = true;

  /**
   * Should use section be hidden?
   */
  public hideUseSection = false;

  /**
   * Should last sale section be hidden?
   */
  public hideLastSaleSection = false;

  /**
   * Delete current tax row subject
   */
  private deleteCurrentRowSubject = new Subject<void>();

  /**
   * Delete current tax row observable
   */
  public deleteCurrentRow$ = this.deleteCurrentRowSubject.asObservable();

  /**
   * Add tax row subject
   */
  private addRowTaxSubject = new Subject<any>();

  /**
   * Add tax row observable
   */
  public addRowTax$ = this.addRowTaxSubject.asObservable();

  /**
   * Move current row up subject
   */
  private moveCurrentRowUpSubject = new Subject<void>();

  /**
   * Move current row up observable
   */
  public moveCurrentRowUp$ = this.moveCurrentRowUpSubject.asObservable();

  /**
   * Move current row down subject
   */
  private moveCurrentRowDownSubject = new Subject<void>();

  /**
   * Move current row down observable
   */
  public moveCurrentRowDown$ = this.moveCurrentRowDownSubject.asObservable();

  /**
   * Delete currentlast sale row subject
   */
  private deleteCurrentRowSubjectLastSale = new Subject<void>();

  /**
   * Delete current last sale row observable
   */
  public deleteCurrentRowLastSale$ = this.deleteCurrentRowSubjectLastSale.asObservable();

  /**
   * Add last sale row subject
   */
  private addRowSubjectLastSale = new Subject<any>();

  /**
   * Add last sale row observable
   */
  public addRowLastSale$ = this.addRowSubjectLastSale.asObservable();

  /**
   * Move current last sale row up subject
   */
  private moveCurrentRowUpSubjectLastSale = new Subject<void>();

  /**
   * Move current last sale row up observable
   */
  public moveCurrentRowUpLastSale$ = this.moveCurrentRowUpSubjectLastSale.asObservable();

  /**
   * Move current last sale row down subject
   */
  private moveCurrentRowDownSubjectLastSale = new Subject<void>();

  /**
   * Move current last sale row down observable
   */
  public moveCurrentRowDownLastSale$ = this.moveCurrentRowDownSubjectLastSale.asObservable();

  /**
   * Delete current use row subject
   */
  private deleteCurrentRowSubjectUse = new Subject<void>();

  /**
   * Delete current use row observable
   */
  public deleteCurrentRowUse$ = this.deleteCurrentRowSubjectUse.asObservable();

  /**
   * Add use row subject
   */
  private addRowSubjectUse = new Subject<any>();

  /**
   * Add use row observable
   */
  public addRowUse$ = this.addRowSubjectUse.asObservable();

  /**
   * Move current use row up subject
   */
  private moveCurrentRowUpSubjectUse = new Subject<void>();

  /**
   * Move current use row up observable
   */
  public moveCurrentRowUpUse$ = this.moveCurrentRowUpSubjectUse.asObservable();

  /**
   * Move current last sale row down subject
   */
  private moveCurrentRowDownSubjectUse = new Subject<void>();

  /**
   * Move current use row down observable
   */
  public moveCurrentRowDownUse$ = this.moveCurrentRowDownSubjectUse.asObservable();

  /**
   * Text & List tax section rows
   */
  rowsTax: any = [];

  /**
   * Text & List tax section name
   */
  readonly textAndListSectionNameTax: string;

  /**
   * Text & List last sale section rows
   */
  rowsLastSale: any = [];

  /**
   * Text & List last sale section name
   */
  readonly textAndListSectionNameLastSale: string;

  /**
   * Text & List use section rows
   */
  rowsUse: any = [];

  /**
   * Text & List use section name
   */
  readonly textAndListSectionNameUse: string;

  /**
  * Constructor
  */
  constructor(injector: Injector) {
    super(injector, 'countrySpecifics', false);
    this.textAndListSectionNameTax = `${this.name}-tax`;
    this.textAndListSectionNameLastSale = `${this.name}-lastsale`;
    this.textAndListSectionNameUse = `${this.name}-use`;
  }

  /**
   * Widget's text area options (if used)
   */
  textAreaOptions: any = null;

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready

    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler("countrySpecifics", this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    if (!this.widgetConfig.hashtagSupported) {
      this.widgetConfig.hashtagSupported = true;
    }
    this.initializeUseSection();
    this.initializeTaxSection();
    this.initializeLastSaleSection();

    this.loadReflist('ref_l3_country_specifics_tax', !this.disableCustomLabels);
    this.loadReflist('ref_l3_country_specifics_last_sale', !this.disableCustomLabels);
    this.loadReflist('ref_l3_country_specifics_use', !this.disableCustomLabels);

    this.mockupData$ = this.widgetService.loadMockup('countrySpecifics').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('countrySpecifics', this.getReport()).pipe();
  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  protected preprocessLoadedData(data) {
    //#17474: for backward compatibility restrict ref_l3_country_specifics_use to oids 1, 2, and 3
    this.reflists.ref_l3_country_specifics_use = this.reflists.ref_l3_country_specifics_use.filter((item) => item.oid <= 3);

    //#17470: similar for last sale (oids IN [1, 6])
    this.reflists.ref_l3_country_specifics_last_sale = this.reflists.ref_l3_country_specifics_last_sale.filter((item) => item.oid <= 6);

    if ((this.widgetConfig.widgetVersion || 1) >= 2) {
      //hide "Highest and Best Use" for newly added widgets (display just for already added in past)
      //as of 20230502 "Highest and Best Use" has its own widget
      this.hideUseSection = true;

      //hide "Last Sale" for newly added widgets (display just for already added in past)
      //as of 20230503 "Last Sale" has its own widget
      this.hideLastSaleSection = true;
    }

    return data;
  }

  /**
   * Initializes the Text & List tax section
   */
  private initializeTaxSection() {
    this.rowsTax = this.getTextAndListSettingsOrSetDefault(this.textAndListSectionNameTax, [
      {
        topicId: 1,
        fieldName: 'assesedValue',
        isTextField: false,
        isNumericField: true,
        isDateField: false,
        lastTopicId: 1,
        decimals: 2,
        selected: true
      }, {
        topicId: 2,
        fieldName: 'taxRate',
        isNumericField: true,
        decimals: 2,
        isTextField: false,
        isDateField: false,
        lastTopicId: 2,
        selected: true
      }, {
        topicId: 3,
        fieldName: 'currentLiablillity',
        isNumericField: true,
        isTextField: false,
        isDateField: false,
        lastTopicId: 3,
        selected: true
      }
    ]);
  }

  /**
   * Initializes the Text & List last sale section
   */
  private initializeLastSaleSection() {
    this.rowsLastSale = this.getTextAndListSettingsOrSetDefault(this.textAndListSectionNameLastSale, [
      {
        topicId: 1,
        fieldName: 'saleDate',
        isDateField: true,
        isNumericField: false,
        isTextField: false,
        lastTopicId: 1,
        selected: true
      }, {
        topicId: 2,
        fieldName: 'saleCondition',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 2,
        selected: true
      }, {
        topicId: 3,
        fieldName: 'salePrice',
        isNumericField: true,
        isTextField: false,
        isDateField: false,
        lastTopicId: 3,
        selected: true
      }, {
        topicId: 4,
        fieldName: 'seller',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 4,
        selected: true
      }, {
        topicId: 5,
        fieldName: 'buyer',
        isTextField: true,
        isNumericField: false,
        isDateField: false,
        lastTopicId: 5,
        selected: true
      }, {
        topicId: 6,
        fieldName: 'recordIdentifier',
        isTextField: true,
        isNumericField: false,
        ifDateField: false,
        lastTopicId: 6,
        selected: true
      }
    ]);
  }

  /**
   * Initializes the Text & List use section
   */
  private initializeUseSection() {
    this.rowsUse = this.getTextAndListSettingsOrSetDefault(this.textAndListSectionNameUse, [
      {
        topicId: 1,
        fieldName: 'mostProbableBuyer',
        isTextField: true,
        lastTopicId: 1,
        selected: true
      }, {
        topicId: 2,
        fieldName: 'asVanant',
        isTextField: true,
        lastTopicId: 2,
        selected: true
      }, {
        topicId: 3,
        fieldName: 'asImproved',
        isTextField: true,
        lastTopicId: 3,
        selected: true
      },
    ]);
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any = null): void {
    //do something if error
  }

  /**
   * Delete current row event handler
   * @param rows {any[]} Rows
   * @param section {string} Section
   */
  public deleteCurrentRow(rows: any[], section: string): void {
    if (section === 'tax') {
      this.setTextAndListSettings(this.textAndListSectionNameTax, rows);
      this.deleteCurrentRowSubject.next();
    } else if (section === 'sale') {
      this.setTextAndListSettings(this.textAndListSectionNameLastSale, rows);
      this.deleteCurrentRowSubjectLastSale.next();
    } else if (section === 'use') {
      this.setTextAndListSettings(this.textAndListSectionNameUse, rows);
      this.deleteCurrentRowSubjectUse.next();
    }
  }

  /**
   * Add row event handler
   * @param rows {any[]} Rows
   * @param section {string} Section
   */
  public addRow(rows: any[], section: string): void {
    if (section === 'tax') {
      const unselectedFields = this.rowsTax.filter(item => !item.selected);
      if (unselectedFields.length > 0) {
        this.setTextAndListSettings(this.textAndListSectionNameTax, rows);
        this.addRowTaxSubject.next({
          topicId: this.getTopicId(section),
          fieldName: null,
          isNumericField: true,
          isTextField: false,
          lastTopicId: this.getTopicId(section),
          selected: true
        });
      }
    } else if (section === 'sale') {
      const unselectedFields = this.rowsLastSale.filter(item => !item.selected);
      if (unselectedFields.length > 0) {
        this.setTextAndListSettings(this.textAndListSectionNameLastSale, rows);
        this.addRowSubjectLastSale.next({
          topicId: this.getTopicId(section),
          fieldName: null,
          isTextField: true,
          isNumericField: false,
          isDataField: false,
          lastTopicId: this.getTopicId(section),
          selected: true
        });
      }
      } else if (section === 'use') {
      const unselectedFields = this.rowsUse.filter(item => !item.selected);
        if (unselectedFields.length > 0) {
          this.setTextAndListSettings(this.textAndListSectionNameUse, rows);
          this.addRowSubjectUse.next({
            topicId: this.getTopicId(section),
            fieldName: null,
            isTextField: true,
            isNumericField: false,
            isDataField: false,
            lastTopicId: this.getTopicId(section),
            selected: true
          });
        }
    }
  }

  /**
   * Move current row up event handler
   * @param rows {any[]} Rows
   * @param section {string} Section
   */
  public moveCurrentRowUp(rows: any[], section: string): void {
    if (section === 'tax') {
      this.setTextAndListSettings(this.textAndListSectionNameTax, rows);
      this.moveCurrentRowUpSubject.next();
    } else if (section === 'sale') {
      this.setTextAndListSettings(this.textAndListSectionNameLastSale, rows);
      this.moveCurrentRowUpSubjectLastSale.next();
    } else if (section === 'use') {
      this.setTextAndListSettings(this.textAndListSectionNameUse, rows);
      this.moveCurrentRowUpSubjectUse.next();
    }
  }

  /**
   * Move current row down event handler
   * @param rows {any[]} Rows
   * @param section {string} Section
   */
  public moveCurrentRowDown(rows: any[], section: string): void {
    if (section === 'tax') {
      this.setTextAndListSettings(this.textAndListSectionNameTax, rows);
      this.moveCurrentRowDownSubject.next();
    } else if (section === 'sale') {
      this.setTextAndListSettings(this.textAndListSectionNameLastSale, rows);
      this.moveCurrentRowDownSubjectLastSale.next();
    } else if (section === 'use') {
      this.setTextAndListSettings(this.textAndListSectionNameUse, rows);
      this.moveCurrentRowDownSubjectUse.next();
    }
  }

  /**
   * Data changed event handler
   * @param rows {any[]} Rows
   */
  public onDataChanged(rows: any[]): void {
    this.setTextAndListSettings(this.textAndListSectionNameTax, this.rowsTax);
    this.setTextAndListSettings(this.textAndListSectionNameLastSale, this.rowsLastSale);
    this.setTextAndListSettings(this.textAndListSectionNameUse, this.rowsUse);
    this.setDirtyFlag(true);
  }

  /**
   * Get topic Id
   * @param section {string} Section name
   */
  private getTopicId(section: string) {
    if (section === 'tax') {
      const unselectedFields = this.rowsTax.filter(item => !item.selected);
      return Math.min(...unselectedFields.map(item => item.topicId));
    } else if (section === 'sale') {
      const unselectedFields = this.rowsLastSale.filter(item => !item.selected);
      return Math.min(...unselectedFields.map(item => item.topicId));
    } else if (section === 'use') {
      const unselectedFields = this.rowsUse.filter(item => !item.selected);
      return Math.min(...unselectedFields.map(item => item.topicId));
    }
  }

}
